export type EstadoCliente = 'activo' | 'inactivo';

export type FaseProyecto = 'soporte_tecnico' | 'fase_final' | 'nuevos_modulos';

export type EstadoTarea = 'backlog' | 'por_hacer' | 'en_progreso' | 'esperando_cliente' | 'testing' | 'produccion';

export type TipoTarea = 'bug_soporte' | 'nuevo_modulo' | 'actualizacion_refactor';

export type Prioridad = 'urgente' | 'alta' | 'moderada' | 'baja';

export type Tamanio = 'S' | 'M' | 'L' | 'XL';

export type ClaseServicio = 'estandar' | 'expedito' | 'fecha_fija';

export interface Cliente {
    id: number;
    nombre: string;
    empresa: string;
    color: string;
    estado: EstadoCliente;
    creado_en: string;
}

export interface DocumentoCliente {
    id: number;
    cliente_id: number;
    tarea_id: number | null;
    nombre_original: string;
    nombre_archivo: string;
    mime_type: string;
    tamanio_bytes: number;
    subido_en: string;
    tarea_titulo?: string | null;
    tarea_estado?: EstadoTarea | null;
}

export interface Proyecto {
    id: number;
    cliente_id: number;
    nombre_proyecto: string;
    fase: FaseProyecto;
    creado_en: string;
}

export interface Tarea {
    id: number;
    proyecto_id: number;
    titulo: string;
    descripcion: string | null;
    estado: EstadoTarea;
    tipo_tarea: TipoTarea;
    prioridad: Prioridad;
    tamanio: Tamanio;
    clase_servicio: ClaseServicio;
    responsable: string | null;
    bloqueado: boolean;
    motivo_bloqueo: string | null;
    archivado: boolean;
    fecha_limite: string | null;
    creado_en: string;
    actualizado_en: string;
    // Audio del que se originó esta tarea (vía "Crear tarea" desde
    // Audios), si corresponde — null si se creó a mano en el tablero.
    audio_origen_id: number | null;
}

export interface TareaConRelaciones extends Tarea {
    nombre_proyecto: string;
    cliente_id: number;
    cliente_nombre: string;
    empresa: string;
    cliente_color: string;
    fase: FaseProyecto;
}

export interface BacklogItem extends TareaConRelaciones {
    dias_en_backlog: number;
}

export interface TareaHistorial {
    id: number;
    tarea_id: number;
    estado_anterior: EstadoTarea | null;
    estado_nuevo: EstadoTarea;
    fecha_cambio: string;
}

export interface WipLimite {
    estado: EstadoTarea;
    limite: number | null;
}

export type RolUsuario = 'gerente' | 'supervisor' | 'operador';

export type ModuloSistema = 'kanban' | 'clientes' | 'documentos' | 'audios' | 'reportes' | 'configuracion' | 'ventas';

export const MODULOS_SISTEMA: ModuloSistema[] = ['kanban', 'clientes', 'documentos', 'audios', 'reportes', 'configuracion', 'ventas'];

export const MODULO_LABELS: Record<ModuloSistema, string> = {
    kanban: 'Kanban',
    clientes: 'Clientes',
    documentos: 'Documentos',
    audios: 'Audios',
    reportes: 'Reportes',
    configuracion: 'Configuración',
    ventas: 'Ventas (Coach IA)'
};

export const ROL_LABELS: Record<RolUsuario, string> = {
    gerente: 'Gerente',
    supervisor: 'Supervisor',
    operador: 'Operador'
};

export interface Usuario {
    id: number;
    nombre: string;
    email: string;
    rol: RolUsuario;
    permisos_modulos: ModuloSistema[];
    telegram_chat_id: number | null;
    activo: boolean;
    creado_en: string;
}

export interface AuthResponse {
    usuario: Usuario;
    token: string;
}

export interface TiempoRegistro {
    id: number;
    tarea_id: number;
    usuario_id: number;
    inicio: string;
    fin: string | null;
    duracion_segundos: number | null;
    nota: string | null;
    creado_en: string;
}

export interface TiempoData {
    registros: TiempoRegistro[];
    total_segundos: number;
    activo: TiempoRegistro | null;
}

export interface Notificacion {
    id: number;
    usuario_id: number;
    tipo: string;
    titulo: string;
    mensaje: string;
    referencia_id: number | null;
    referencia_tipo: string | null;
    leida: boolean;
    creado_en: string;
}

export interface NotificacionesData {
    items: Notificacion[];
    no_leidas: number;
}

export interface Metricas {
    lead_time_promedio_dias: number | null;
    cycle_time_promedio_dias: number | null;
    throughput_7d: number;
    throughput_30d: number;
    total_completadas: number;
}

export interface CfdPunto {
    fecha: string;
    backlog: number;
    por_hacer: number;
    en_progreso: number;
    esperando_cliente: number;
    testing: number;
    produccion: number;
}

export interface BloqueosReporte {
    total_bloqueados: number;
    por_columna: Partial<Record<EstadoTarea, number>>;
    tiempo_promedio_bloqueado_dias: number | null;
    bloqueos_por_cliente: Array<{ cliente_id: number; empresa: string; color: string; total: number }>;
    items: Array<{
        id: number;
        titulo: string;
        estado: EstadoTarea;
        dias_bloqueado: number | null;
        motivo: string | null;
        empresa: string;
        cliente_color: string;
    }>;
}

export interface ThroughputPunto {
    fecha: string;
    completadas: number;
}

export interface WipAgingItem {
    id: number;
    titulo: string;
    columna: EstadoTarea;
    dias_en_columna: number;
    cliente_nombre: string;
    empresa: string;
    cliente_color: string;
    responsable: string | null;
    bloqueado: boolean;
    clase_servicio: ClaseServicio;
    prioridad: Prioridad;
}

export interface AlertasReporte {
    bloqueados_mas_3d: number;
    bloqueados_total: number;
    wip_al_maximo: Array<{ estado: EstadoTarea; limite: number; actual: number }>;
    backlog_mas_90d: number;
    backlog_30a90d: number;
    items: Array<{
        id: number;
        titulo: string;
        tipo: 'bloqueo_largo' | 'wip_max' | 'backlog_90d' | 'backlog_30_90d';
        detalle: string;
    }>;
}

export interface StandupResumen {
    fecha: string;
    items_producidos_recientes: Array<{ id: number; titulo: string; empresa: string }>;
    testing_estancados: Array<{ id: number; titulo: string; dias: number; empresa: string }>;
    en_progreso_estancados: Array<{ id: number; titulo: string; dias: number; empresa: string; responsable: string | null }>;
    esperando_cliente: Array<{ id: number; titulo: string; dias: number; empresa: string; motivo: string | null }>;
    bloqueos_nuevos_hoy: number;
    cupo_por_hacer: { actual: number; limite: number | null };
}

export interface ReplenishSugerencia {
    items: Array<{
        id: number;
        titulo: string;
        prioridad: Prioridad;
        clase_servicio: ClaseServicio;
        dias_en_backlog: number;
        empresa: string;
        cliente_color: string;
        score: number;
        razon: string;
    }>;
}

export interface RetrospectivaData {
    desde: string;
    hasta: string;
    completadas: number;
    cycle_time_promedio_dias: number | null;
    throughput_semanal: number;
    bloqueos_totales: number;
    bloqueos_frecuentes_por_columna: Partial<Record<EstadoTarea, number>>;
    items_mas_largos: Array<{ id: number; titulo: string; dias: number; empresa: string }>;
    items_archivados: number;
}

export interface Politica {
    contenido: string;
    actualizado_en: string;
}

export interface ApiResponse<T> {
    success: boolean;
    data?: T;
    error?: string;
    message?: string;
}

export interface KanbanSwimlane {
    fase: FaseProyecto;
    tareas: Record<EstadoTarea, TareaConRelaciones[]>;
}

export const STATUS_GROUPS: Record<string, EstadoTarea[]> = {
    'to_do': ['backlog', 'por_hacer'],
    'in_progress': ['en_progreso', 'esperando_cliente'],
    'complete': ['testing', 'produccion']
};

export const STATUS_GROUP_NAMES: Record<string, string> = {
    'to_do': 'To Do',
    'in_progress': 'In Progress',
    'complete': 'Complete'
};

export const FASE_LABELS: Record<FaseProyecto, string> = {
    soporte_tecnico: '1. Soporte Técnico (Producción)',
    fase_final: '2. Fase Final (Casi en producción)',
    nuevos_modulos: '3. Nuevos Módulos (Desarrollo)'
};

export const FASE_ORDER: FaseProyecto[] = ['soporte_tecnico', 'fase_final', 'nuevos_modulos'];

export const ESTADO_LABELS: Record<EstadoTarea, string> = {
    backlog: 'Backlog',
    por_hacer: 'Por Hacer',
    en_progreso: 'En Progreso',
    esperando_cliente: 'Esperando Cliente',
    testing: 'Testing / QA',
    produccion: 'Producción / Listo'
};

export const COLUMNS_ORDER: EstadoTarea[] = ['backlog', 'por_hacer', 'en_progreso', 'esperando_cliente', 'testing', 'produccion'];

export const TIPO_TAREA_LABELS: Record<TipoTarea, string> = {
    bug_soporte: '🐛 Bug / Soporte',
    nuevo_modulo: '🚀 Nuevo Módulo',
    actualizacion_refactor: '🔄 Actualización / Refactor'
};

export const PRIORIDAD_LABELS: Record<Prioridad, string> = {
    urgente: 'Urgente',
    alta: 'Alta',
    moderada: 'Moderada',
    baja: 'Baja'
};

export const PRIORIDAD_COLORS: Record<Prioridad, { bg: string; text: string; ring: string; icon: string; order: number }> = {
    urgente:  { bg: 'bg-red-100',    text: 'text-red-700',    ring: 'ring-red-400',    icon: '🔥', order: 0 },
    alta:     { bg: 'bg-orange-100', text: 'text-orange-700', ring: 'ring-orange-400', icon: '!',  order: 1 },
    moderada: { bg: 'bg-amber-100',  text: 'text-amber-700',  ring: 'ring-amber-400',  icon: '~',  order: 2 },
    baja:     { bg: 'bg-slate-100',  text: 'text-slate-600',  ring: 'ring-slate-300',  icon: '–',  order: 3 }
};

export const TAMANIO_LABELS: Record<Tamanio, string> = {
    S: 'S',
    M: 'M',
    L: 'L',
    XL: 'XL'
};

export const CLASE_SERVICIO_LABELS: Record<ClaseServicio, string> = {
    estandar: 'Estándar',
    expedito: 'Expedito',
    fecha_fija: 'Fecha Fija'
};

export const CLASE_SERVICIO_ICONS: Record<ClaseServicio, string> = {
    estandar: '',
    expedito: '⚡',
    fecha_fija: '📌'
};

export const ESTADO_CLIENTE_LABELS: Record<EstadoCliente, string> = {
    activo: 'Activo',
    inactivo: 'Inactivo'
};

export type EstadoAudio = 'recibido' | 'transcribiendo' | 'analizando' | 'pendiente_revision' | 'aprobado' | 'error';

export type IntencionAudio = 'bug' | 'feature' | 'consulta' | 'reclamo' | 'otro';

export type UrgenciaAudio = 'baja' | 'media' | 'alta' | 'critica';

export interface AnalisisAudio {
    id: number;
    audio_id: number;
    transcripcion: string;
    intencion: IntencionAudio;
    sistema_afectado: string | null;
    urgencia: UrgenciaAudio;
    resumen: string;
    respuesta_sugerida: string;
    respuesta_final: string | null;
    tarea_id: number | null;
    modelo_asr: string | null;
    modelo_llm: string | null;
    version: number;
}

export interface AudioCliente {
    id: number;
    cliente_id: number;
    ruta_archivo: string;
    duracion_segundos: number | null;
    origen: 'upload_manual' | 'whatsapp' | 'otro';
    estado: EstadoAudio;
    // Tarea del Kanban creada automáticamente al subir el audio.
    tarea_id: number | null;
    error_detalle: string | null;
    version: number;
    creado_en: string;
    actualizado_en: string;
    cliente_nombre: string;
    empresa: string;
    cliente_color: string;
    analisis: AnalisisAudio | null;
}

export interface ListaAudios {
    items: AudioCliente[];
    total: number;
    pagina: number;
    por_pagina: number;
}

// ── Observabilidad de la cola de audios (worker de transcripción/análisis) ──

export interface AudioColaError {
    id: number;
    cliente_nombre: string;
    error_detalle: string | null;
    creado_en: string;
    actualizado_en: string;
}

export interface ColaAudiosEstado {
    conteos: Record<EstadoAudio, number>;
    en_cola: number;
    mas_antiguo_pendiente_minutos: number | null;
    worker_configurado: boolean;
    errores_recientes: AudioColaError[];
}

export const ESTADO_AUDIO_LABELS: Record<EstadoAudio, string> = {
    recibido: 'Recibido',
    transcribiendo: 'Transcribiendo',
    analizando: 'Analizando',
    pendiente_revision: 'Pendiente de revisión',
    aprobado: 'Aprobado',
    error: 'Error'
};

export const ESTADO_AUDIO_COLORS: Record<EstadoAudio, string> = {
    recibido: 'bg-slate-100 text-slate-700',
    transcribiendo: 'bg-blue-100 text-blue-700',
    analizando: 'bg-blue-100 text-blue-700',
    pendiente_revision: 'bg-amber-100 text-amber-700',
    aprobado: 'bg-emerald-100 text-emerald-700',
    error: 'bg-red-100 text-red-700'
};

export const URGENCIA_AUDIO_LABELS: Record<UrgenciaAudio, string> = {
    baja: 'Baja',
    media: 'Media',
    alta: 'Alta',
    critica: 'Crítica'
};

export const URGENCIA_AUDIO_COLORS: Record<UrgenciaAudio, string> = {
    baja: 'bg-emerald-100 text-emerald-700',
    media: 'bg-amber-100 text-amber-700',
    alta: 'bg-orange-100 text-orange-700',
    critica: 'bg-red-100 text-red-700'
};

export const INTENCION_AUDIO_LABELS: Record<IntencionAudio, string> = {
    bug: '🐛 Bug',
    feature: '🚀 Feature',
    consulta: '❓ Consulta',
    reclamo: '⚠️ Reclamo',
    otro: '📝 Otro'
};

export const ESTADOS_AUDIO_INTERMEDIOS: EstadoAudio[] = ['recibido', 'transcribiendo', 'analizando'];

// ── Coach de Llamadas de Venta (módulo 'ventas') ────────────────────────

export type EstadoLlamada = 'recibido' | 'transcribiendo' | 'analizando' | 'listo' | 'error';

export type TemperaturaLead = 'frio' | 'tibio' | 'caliente';

export interface ErrorCriticoLlamada {
    tipo: string;
    descripcion: string;
    momento_transcripcion: string;
    correccion_sugerida: string;
}

export interface AnalisisLlamada {
    id: number;
    llamada_id: number;
    transcripcion: string;
    resumen_conversacion: string;
    temperatura_lead: TemperaturaLead;
    escucha_activa: number;
    calificacion_dolor: number;
    manejo_objeciones: number;
    claridad_propuesta: number;
    tecnicas_cierre: number;
    puntos_fuertes: string[];
    errores_criticos: ErrorCriticoLlamada[];
    oportunidades_cierre_perdidas: string[];
    plan_accion_vendedor: string[];
    modelo_asr: string | null;
    modelo_llm: string | null;
    version: number;
}

export interface LlamadaVenta {
    id: number;
    cliente_id: number;
    ruta_archivo: string;
    producto_servicio: string | null;
    duracion_segundos: number | null;
    estado: EstadoLlamada;
    error_detalle: string | null;
    version: number;
    creado_en: string;
    actualizado_en: string;
    cliente_nombre: string;
    empresa: string;
    cliente_color: string;
    analisis: AnalisisLlamada | null;
}

export interface ListaLlamadasVenta {
    items: LlamadaVenta[];
    total: number;
    pagina: number;
    por_pagina: number;
}

export const ESTADO_LLAMADA_LABELS: Record<EstadoLlamada, string> = {
    recibido: 'Recibido',
    transcribiendo: 'Transcribiendo',
    analizando: 'Analizando',
    listo: 'Listo',
    error: 'Error'
};

export const ESTADO_LLAMADA_COLORS: Record<EstadoLlamada, string> = {
    recibido: 'bg-slate-100 text-slate-700',
    transcribiendo: 'bg-blue-100 text-blue-700',
    analizando: 'bg-blue-100 text-blue-700',
    listo: 'bg-emerald-100 text-emerald-700',
    error: 'bg-red-100 text-red-700'
};

export const ESTADOS_LLAMADA_INTERMEDIOS: EstadoLlamada[] = ['recibido', 'transcribiendo', 'analizando'];

export const TEMPERATURA_LEAD_LABELS: Record<TemperaturaLead, string> = {
    frio: '🧊 Frío',
    tibio: '🌤️ Tibio',
    caliente: '🔥 Caliente'
};

export const TEMPERATURA_LEAD_COLORS: Record<TemperaturaLead, string> = {
    frio: 'bg-sky-100 text-sky-700',
    tibio: 'bg-amber-100 text-amber-700',
    caliente: 'bg-red-100 text-red-700'
};

export const METRICAS_RENDIMIENTO_LABELS: Record<string, string> = {
    escucha_activa: 'Escucha activa',
    calificacion_dolor: 'Calificación del dolor',
    manejo_objeciones: 'Manejo de objeciones',
    claridad_propuesta: 'Claridad de la propuesta',
    tecnicas_cierre: 'Técnicas de cierre'
};

// ── Analista de Datos (planillas + chat, módulo 'ventas') ───────────────

export type EstadoDatoAnalisis = 'recibido' | 'procesando' | 'listo' | 'error';

export interface DatoAnalisis {
    id: number;
    cliente_id: number;
    nombre_original: string;
    mime_type: string;
    tamanio_bytes: number;
    estado: EstadoDatoAnalisis;
    contenido_extraido: string | null;
    error_detalle: string | null;
    version: number;
    creado_en: string;
}

export const ESTADO_DATO_LABELS: Record<EstadoDatoAnalisis, string> = {
    recibido: 'Recibido',
    procesando: 'Procesando',
    listo: 'Listo',
    error: 'Error'
};

export const ESTADO_DATO_COLORS: Record<EstadoDatoAnalisis, string> = {
    recibido: 'bg-slate-100 text-slate-700',
    procesando: 'bg-blue-100 text-blue-700',
    listo: 'bg-emerald-100 text-emerald-700',
    error: 'bg-red-100 text-red-700'
};

export type RolMensajeChat = 'user' | 'assistant';

export interface ChatAnalistaMensaje {
    id: number;
    cliente_id: number;
    rol: RolMensajeChat;
    contenido: string;
    creado_en: string;
}
